<template>
  <Teleport to="body">
    <transition name="alice-modal">
      <div
        v-if="dialog"
        class="alice-modal"
        role="presentation"
        @click.self="$emit('close')"
      >
        <div
          class="alice-modal__card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="alice-tags-title"
        >
          <header class="alice-modal__head">
            <div>
              <div class="alice-modal__eyebrow">Tags</div>
              <h2 id="alice-tags-title" class="alice-modal__title">
                {{ recording?.name || "Recording" }}
              </h2>
            </div>
            <button
              class="alice-modal__close"
              type="button"
              title="Close"
              @click="$emit('close')"
            >
              ×
            </button>
          </header>

          <div class="alice-modal__body">
            <p class="alice-modal__desc">
              Add key:value tags to describe this participant or session (e.g.
              <strong>gender: female</strong>, <strong>device: mobile</strong>).
              These are sent with the chat context so you can compare subgroups.
            </p>

            <div v-if="localTags.length" class="alice-modal__rows">
              <div
                v-for="(t, i) in localTags"
                :key="i"
                class="alice-modal__row"
              >
                <input
                  v-model="t.key"
                  class="alice-modal__input alice-modal__input--key"
                  type="text"
                  list="alice-tag-keys"
                  placeholder="Key"
                />
                <span class="alice-modal__colon">:</span>
                <input
                  v-model="t.value"
                  class="alice-modal__input"
                  type="text"
                  placeholder="Value"
                />
                <button
                  class="alice-modal__remove"
                  type="button"
                  title="Remove tag"
                  @click="removeTag(i)"
                >
                  <Icon name="trash" :size="14" />
                </button>
              </div>
            </div>
            <p v-else class="alice-modal__empty">No tags yet.</p>

            <datalist id="alice-tag-keys">
              <option v-for="k in existingKeys" :key="k" :value="k" />
            </datalist>

            <button class="alice-modal__add" type="button" @click="addTag">
              <Icon name="plus" :size="14" />
              <span>Add tag</span>
            </button>
          </div>

          <footer class="alice-modal__foot">
            <button
              class="alice-modal__btn alice-modal__btn--ghost"
              type="button"
              @click="$emit('close')"
            >
              Cancel
            </button>
            <button
              class="alice-modal__btn alice-modal__btn--primary"
              type="button"
              :disabled="saving"
              @click="save"
            >
              {{ saving ? "Saving…" : "Save" }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import Icon from "@/components/alice/Icon.vue";

export default {
  name: "RecordingTagsDialog",
  components: { Icon },
  props: {
    dialog: { type: Boolean, default: false },
    recording: { type: Object, default: null },
    // All tag keys already used across the project's recordings, offered as
    // suggestions so subgroup keys stay consistent (e.g. always "gender").
    existingKeys: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  emits: ["close", "save"],
  data() {
    return {
      localTags: [],
    };
  },
  watch: {
    // Re-seed the editable copy whenever the dialog opens for a recording.
    dialog(open) {
      if (open) this.seed();
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape" && this.dialog) this.$emit("close");
    },
    seed() {
      const tags = Array.isArray(this.recording?.tags)
        ? this.recording.tags
        : [];
      this.localTags = tags.map((t) => ({
        key: t.key || "",
        value: t.value || "",
      }));
    },
    addTag() {
      this.localTags.push({ key: "", value: "" });
    },
    removeTag(index) {
      this.localTags.splice(index, 1);
    },
    save() {
      // Drop empty rows and trim; keep only well-formed key:value pairs.
      const cleaned = this.localTags
        .map((t) => ({
          key: String(t.key || "").trim(),
          value: String(t.value || "").trim(),
        }))
        .filter((t) => t.key && t.value);
      this.$emit("save", cleaned);
    },
  },
};
</script>

<style scoped lang="scss">
.alice-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}
.alice-modal__card {
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
}

/* ---- header ---- */
.alice-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-modal__eyebrow {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 6px;
}
.alice-modal__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: var(--vp-black);
}
.alice-modal__close {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1;
  color: var(--vp-black);
}
.alice-modal__close:hover {
  border-color: var(--vp-black);
}

/* ---- body ---- */
.alice-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}
.alice-modal__desc {
  margin: 0 0 18px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--fg-2);
}
.alice-modal__desc strong {
  color: var(--vp-black);
}
.alice-modal__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}
.alice-modal__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.alice-modal__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  outline: none;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--vp-black);
  transition: border-color 0.12s ease;
}
.alice-modal__input::placeholder {
  color: var(--fg-3);
}
.alice-modal__input:focus {
  border-color: var(--vp-black);
}
.alice-modal__input--key {
  font-family: var(--font-ui);
  font-weight: 600;
}
.alice-modal__colon {
  flex-shrink: 0;
  font-family: var(--font-mono);
  color: var(--fg-3);
}
.alice-modal__remove {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  color: var(--vp-rood-100);
  transition: border-color 0.12s ease, background 0.12s ease;
}
.alice-modal__remove:hover {
  border-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}
.alice-modal__empty {
  margin: 0 0 16px;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-3);
}
.alice-modal__add {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: transparent;
  border: 1px dashed var(--vp-grijs-60);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
  transition: border-color 0.12s ease, color 0.12s ease;
}
.alice-modal__add:hover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
}

/* ---- footer ---- */
.alice-modal__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border-hairline);
}
.alice-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 22px;
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.alice-modal__btn--ghost {
  background: transparent;
  border: 1px solid transparent;
  color: var(--fg-2);
}
.alice-modal__btn--ghost:hover {
  border-color: var(--vp-black);
  color: var(--vp-black);
}
.alice-modal__btn--primary {
  background: var(--vp-black);
  border: 1px solid var(--vp-black);
  color: var(--vp-white);
}
.alice-modal__btn--primary:hover:not(:disabled) {
  background: var(--accent-100);
  border-color: var(--accent-100);
  color: var(--vp-black);
}
.alice-modal__btn--primary:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ---- transition ---- */
.alice-modal-enter-active,
.alice-modal-leave-active {
  transition: opacity 0.18s ease;
}
.alice-modal-enter-active .alice-modal__card,
.alice-modal-leave-active .alice-modal__card {
  transition: transform 0.18s var(--ease-out, ease), opacity 0.18s ease;
}
.alice-modal-enter-from,
.alice-modal-leave-to {
  opacity: 0;
}
.alice-modal-enter-from .alice-modal__card,
.alice-modal-leave-to .alice-modal__card {
  transform: translateY(12px);
  opacity: 0;
}
</style>
